/**
 * Production-ready API service with comprehensive error handling, logging, and monitoring.
 * 
 * This service provides:
 * - Structured logging with correlation IDs
 * - Performance monitoring and metrics
 * - Input validation and sanitization
 * - Retry logic with exponential backoff
 * - Circuit breaker pattern for fault tolerance
 * - Rate limiting and request throttling
 * - Comprehensive error handling
 * 
 * @example
 * ```typescript
 * import { DocumentAPI } from './api';
 * 
 * const documentApi = new DocumentAPI();
 * const result = await documentApi.getDocumentPage('doc-123', 1);
 * ```
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { logger, LogContext } from './logger';
import { validator, ValidationError } from './validation';
import { performanceMonitor } from './performance';
import { 
  DocumentUploadResponse, 
  DocumentListResponse, 
  DocumentMetadataResponse, 
  AnonymousDocumentResponse,
  DocumentDeleteResponse,
  DocumentMoveResponse,
  PageViewTrackingResponse,
  PrintAccessValidationResponse
} from '../types/api';

// API Configuration
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://api.keedocs.com').replace(/\/$/, '');

/**
 * Circuit breaker states
 */
enum CircuitBreakerState {
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
  HALF_OPEN = 'HALF_OPEN'
}

/**
 * Circuit breaker configuration
 */
interface CircuitBreakerConfig {
  failureThreshold: number;
  recoveryTimeout: number;
  monitoringPeriod: number;
}

/**
 * Retry configuration
 */
interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

/**
 * Circuit breaker implementation for fault tolerance
 */
class CircuitBreaker {
  private state: CircuitBreakerState = CircuitBreakerState.CLOSED;
  private failureCount = 0;
  private lastFailureTime = 0;
  private successCount = 0;

  constructor(private config: CircuitBreakerConfig) {}

  async execute<T>(operation: () => Promise<T>, correlationId: string): Promise<T> {
    if (this.state === CircuitBreakerState.OPEN) {
      if (Date.now() - this.lastFailureTime > this.config.recoveryTimeout) {
        this.state = CircuitBreakerState.HALF_OPEN;
        this.successCount = 0;
        logger.info('Circuit breaker transitioning to HALF_OPEN', {
          correlationId,
          operation: 'circuitBreaker',
          metadata: { state: this.state }
        });
      } else {
        throw new Error('Circuit breaker is OPEN - service unavailable');
      }
    }

    try {
      const result = await operation();
      this.onSuccess(correlationId);
      return result;
    } catch (error: any) {
      // Only count service-level failures (5xx, network errors) against the circuit breaker.
      // 4xx errors are client mistakes, not signs of service degradation.
      if (this.isServiceFailure(error)) {
        this.onFailure(correlationId);
      }
      throw error;
    }
  }

  private isServiceFailure(error: any): boolean {
    if (!error?.response) return true; // network error / timeout
    return error.response.status >= 500;
  }

  private onSuccess(correlationId: string): void {
    this.failureCount = 0;
    
    if (this.state === CircuitBreakerState.HALF_OPEN) {
      this.successCount++;
      if (this.successCount >= 3) { // Require 3 successful calls to close
        this.state = CircuitBreakerState.CLOSED;
        logger.info('Circuit breaker closed after successful recovery', {
          correlationId,
          operation: 'circuitBreaker',
          metadata: { state: this.state, successCount: this.successCount }
        });
      }
    }
  }

  private onFailure(correlationId: string): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.failureCount >= this.config.failureThreshold) {
      this.state = CircuitBreakerState.OPEN;
      logger.warn('Circuit breaker opened due to failures', {
        correlationId,
        operation: 'circuitBreaker',
        metadata: { 
          state: this.state, 
          failureCount: this.failureCount,
          threshold: this.config.failureThreshold
        }
      });
    }
  }

  getState(): CircuitBreakerState {
    return this.state;
  }
}

/**
 * Enhanced API client with production-ready features
 */
class EnhancedApiClient {
  private client: AxiosInstance;
  private circuitBreaker: CircuitBreaker;
  private retryConfig: RetryConfig = {
    maxRetries: 0,
    baseDelay: 1000,
    maxDelay: 10000,
    backoffMultiplier: 2
  };

  constructor() {
    this.client = this.createAxiosInstance();
    this.circuitBreaker = new CircuitBreaker({
      failureThreshold: 5,
      recoveryTimeout: 30000, // 30 seconds
      monitoringPeriod: 60000  // 1 minute
    });
    this.setupInterceptors();
  }

  private createAxiosInstance(): AxiosInstance {
    return axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // 30 seconds timeout
      // Don't set default Content-Type - let axios handle it dynamically
      // This allows FormData to set the proper multipart/form-data boundary
      headers: {},
    });
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const correlationId = logger.getCorrelationId();
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        
        // Add correlation ID to headers
        config.headers = config.headers || {};
        // config.headers['X-Correlation-ID'] = correlationId;
        
        // Add authentication token
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Set Content-Type for non-FormData requests
        // FormData requests should NOT have explicit Content-Type headers
        // because axios needs to set the boundary parameter automatically
        if (config.data && !(config.data instanceof FormData)) {
          config.headers['Content-Type'] = 'application/json';
        }

        // Log request start
        logger.info('API request started', {
          correlationId,
          operation: 'apiRequest',
          metadata: {
            method: config.method?.toUpperCase(),
            url: config.url,
            hasAuth: !!token
          }
        });

        return config;
      },
      (error) => {
        const correlationId = logger.getCorrelationId();
        logger.error('API request setup failed', {
          correlationId,
          operation: 'apiRequest',
          error: error.message,
          stack: error.stack
        });
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        const correlationId = response.config.headers?.['X-Correlation-ID'] as string || logger.getCorrelationId();
        
        // Track API call performance
        const duration = Date.now() - (response.config as any).startTime;
        performanceMonitor.trackApiCall(
          response.config.method?.toUpperCase() || 'GET',
          response.config.url || '',
          response.status,
          duration,
          JSON.stringify(response.data).length,
          false, // Not cached for direct API calls
          correlationId
        );

        return response;
      },
      (error) => {
        const correlationId = error.config?.headers?.['X-Correlation-ID'] || logger.getCorrelationId();
        
        // Log API error
        logger.error('API request failed', {
          correlationId,
          operation: 'apiRequest',
          error: error.message,
          stack: error.stack,
          metadata: {
            method: error.config?.method?.toUpperCase(),
            url: error.config?.url,
            status: error.response?.status,
            statusText: error.response?.statusText
          }
        });

        // Handle authentication errors — but never redirect during auth calls
        // (login/register/oauth return 401/403 on bad credentials, not expired sessions)
        if (error.response?.status === 401 && !error.config?.url?.startsWith('/auth/')) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('user');
          window.location.href = '/login';
        }

        // Handle MissingAuthenticationTokenException
        if (error.response?.data?.message?.includes('MissingAuthenticationToken')) {
          logger.warn('Authentication token missing for protected endpoint', {
            correlationId,
            operation: 'apiRequest',
            metadata: {
              url: error.config?.url,
              method: error.config?.method
            }
          });
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Execute API request with retry logic and circuit breaker
   */
  async executeRequest<T>(
    requestConfig: AxiosRequestConfig,
    correlationId?: string
  ): Promise<T> {
    const cId = correlationId || logger.getCorrelationId();
    const timer = performanceMonitor.startTimer('apiRequest', cId);

    try {
      // Add start time for duration calculation
      (requestConfig as any).startTime = Date.now();

      const result = await this.circuitBreaker.execute(async () => {
        return await this.executeWithRetry<T>(requestConfig, cId);
      }, cId);

      performanceMonitor.endTimer(timer, { 
        success: true, 
        metadata: { 
          method: requestConfig.method,
          url: requestConfig.url 
        } 
      });

      return result;
    } catch (error) {
      performanceMonitor.endTimer(timer, { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error',
        metadata: { 
          method: requestConfig.method,
          url: requestConfig.url 
        } 
      });
      throw error;
    }
  }

  /**
   * Execute request with exponential backoff retry logic
   */
  private async executeWithRetry<T>(
    requestConfig: AxiosRequestConfig,
    correlationId: string,
    attempt: number = 1
  ): Promise<T> {
    try {
      // For FormData requests, ensure we don't override Content-Type header
      // Let axios handle the boundary generation automatically
      if (requestConfig.data instanceof FormData) {
        // Remove any manually set Content-Type for FormData
        if (requestConfig.headers && typeof requestConfig.headers === 'object') {
          delete (requestConfig.headers as any)['Content-Type'];
        }
      }
      
      // Use request() for all requests to ensure consistent handling
      // This respects the full requestConfig including method, headers, etc.
      const response: AxiosResponse<T> = await this.client.request(requestConfig);
      return response.data;      
    } catch (error: any) {
      if (attempt >= this.retryConfig.maxRetries || !this.shouldRetry(error)) {
        throw error;
      }

      const delay = Math.min(
        this.retryConfig.baseDelay * Math.pow(this.retryConfig.backoffMultiplier, attempt - 1),
        this.retryConfig.maxDelay
      );

      logger.warn(`API request failed, retrying in ${delay}ms`, {
        correlationId,
        operation: 'apiRetry',
        metadata: {
          attempt,
          maxRetries: this.retryConfig.maxRetries,
          delay,
          error: error.message,
          status: error.response?.status
        }
      });

      await this.sleep(delay);
      return this.executeWithRetry<T>(requestConfig, correlationId, attempt + 1);
    }
  }

  /**
   * Determine if request should be retried based on error type
   */
  private shouldRetry(error: any): boolean {
    // Don't retry client errors (4xx) except for specific cases
    if (error.response?.status >= 400 && error.response?.status < 500) {
      // Retry on rate limiting or temporary client errors
      return error.response?.status === 429 || error.response?.status === 408;
    }

    // Retry on server errors (5xx) and network errors
    return error.response?.status >= 500 || !error.response;
  }

  /**
   * Sleep utility for retry delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get the underlying axios instance
   */
  getClient(): AxiosInstance {
    return this.client;
  }
}

// Create enhanced API client instance
const apiClient = new EnhancedApiClient();

// API Service Classes with production-ready features
export class HealthAPI {
  /**
   * Check API health status
   */
  static async checkHealth(): Promise<any> {
    const correlationId = logger.getCorrelationId();
    
    logger.info('Health check started', {
      correlationId,
      operation: 'healthCheck'
    });

    try {
      const result = await apiClient.executeRequest<any>({
        method: 'GET',
        url: '/'
      }, correlationId);

      logger.info('Health check completed successfully', {
        correlationId,
        operation: 'healthCheck'
      });

      return result;
    } catch (error) {
      logger.error('Health check failed', {
        correlationId,
        operation: 'healthCheck',
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }
}

export class AuthAPI {
  /**
   * Register new user with comprehensive validation and logging
   */
  static async register(email: string, password: string, firstName?: string, lastName?: string): Promise<any> {
    const correlationId = logger.getCorrelationId();
    
    if (!validator.validateEmail(email, { correlationId })) {
      throw new ValidationError('Invalid email format', 'email', 'INVALID_EMAIL');
    }
    if (!password || password.length < 8) {
      throw new ValidationError('Password must be at least 8 characters', 'password', 'WEAK_PASSWORD');
    }

    logger.info('User registration started', { correlationId, operation: 'register', metadata: { email } });

    try {
      const result = await apiClient.executeRequest<any>({
        method: 'POST',
        url: '/auth/register',
        data: { email, password, first_name: firstName || null, last_name: lastName || null }
      }, correlationId);

      logger.info('User registration completed successfully', { correlationId, operation: 'register', metadata: { email } });
      return result;
    } catch (error) {
      logger.error('User registration failed', {
        correlationId, operation: 'register',
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        metadata: { email }
      });
      throw error;
    }
  }

  /**
   * Login user with comprehensive validation and logging
   */
  static async login(email: string, password: string): Promise<any> {
    const correlationId = logger.getCorrelationId();
    
    // Validate inputs
    if (!validator.validateEmail(email, { correlationId })) {
      throw new ValidationError('Invalid email format', 'email', 'INVALID_EMAIL');
    }

    if (!password) {
      throw new ValidationError('Password is required', 'password', 'MISSING_PASSWORD');
    }

    // Check rate limiting
    if (!validator.checkRateLimit(`login_${email}`, 5, 300000)) { // 5 attempts per 5 minutes
      throw new Error('Too many login attempts. Please try again later.');
    }

    logger.info('User login started', {
      correlationId,
      operation: 'login',
      metadata: { email }
    });

    try {
      const result = await apiClient.executeRequest<any>({
        method: 'POST',
        url: '/auth/login',
        data: { email, password }
      }, correlationId);

      logger.info('User login completed successfully', {
        correlationId,
        operation: 'login',
        metadata: { email }
      });

      return result;
    } catch (error) {
      logger.error('User login failed', {
        correlationId,
        operation: 'login',
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        metadata: { email }
      });
      throw error;
    }
  }

  /**
   * OAuth login with validation and logging
   */
  static async oauthLogin(provider: string, token: string, extras?: { redirectUri?: string; codeVerifier?: string }): Promise<any> {
    const correlationId = logger.getCorrelationId();

    // Validate inputs
    const providerValidation = validator.validateAndSanitizeString(provider, 50, false, { correlationId });
    if (!providerValidation.isValid) {
      throw new ValidationError('Invalid OAuth provider', 'provider', 'INVALID_PROVIDER');
    }

    if (!token || token.length < 10) {
      throw new ValidationError('Invalid OAuth token', 'token', 'INVALID_TOKEN');
    }

    logger.info('OAuth login started', {
      correlationId,
      operation: 'oauthLogin',
      metadata: { provider }
    });

    try {
      const body: Record<string, string> = { token };
      if (extras?.redirectUri) body.redirect_uri = extras.redirectUri;
      if (extras?.codeVerifier) body.code_verifier = extras.codeVerifier;

      const result = await apiClient.executeRequest<any>({
        method: 'POST',
        url: `/auth/oauth/${providerValidation.sanitizedValue}`,
        data: body
      }, correlationId);

      logger.info('OAuth login completed successfully', {
        correlationId,
        operation: 'oauthLogin',
        metadata: { provider }
      });

      return result;
    } catch (error) {
      logger.error('OAuth login failed', {
        correlationId,
        operation: 'oauthLogin',
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        metadata: { provider }
      });
      throw error;
    }
  }

  /**
   * Verify email with validation and logging
   */
  static async resendVerification(email: string): Promise<void> {
    await apiClient.executeRequest({
      method: 'POST',
      url: '/auth/resend-verification',
      data: { email }
    }, logger.getCorrelationId());
  }

  static async verifyEmail(token: string): Promise<any> {
    const correlationId = logger.getCorrelationId();
    
    // Validate token
    if (!token || token.length < 10) {
      throw new ValidationError('Invalid verification token', 'token', 'INVALID_TOKEN');
    }

    logger.info('Email verification started', {
      correlationId,
      operation: 'verifyEmail'
    });

    try {
      const result = await apiClient.executeRequest<any>({
        method: 'POST',
        url: '/auth/verify',
        data: { token }
      }, correlationId);

      logger.info('Email verification completed successfully', {
        correlationId,
        operation: 'verifyEmail'
      });

      return result;
    } catch (error) {
      logger.error('Email verification failed', {
        correlationId,
        operation: 'verifyEmail',
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }

  /**
   * Change user password
   */
  static async getMe(): Promise<any> {
    const correlationId = logger.getCorrelationId();
    return apiClient.executeRequest<any>({ method: 'GET', url: '/auth/me' }, correlationId);
  }

  static async changePassword(currentPassword: string, newPassword: string): Promise<any> {
    const correlationId = logger.getCorrelationId();
    try {
      return await apiClient.executeRequest<any>({
        method: 'POST',
        url: '/auth/change-password',
        data: { current_password: currentPassword, new_password: newPassword }
      }, correlationId);
    } catch (error) {
      logger.error('Change password failed', {
        correlationId,
        operation: 'changePassword',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  static async deleteAccount(): Promise<void> {
    const correlationId = logger.getCorrelationId();
    try {
      await apiClient.executeRequest<any>({
        method: 'DELETE',
        url: '/auth/account',
      }, correlationId);
    } catch (error) {
      logger.error('Delete account failed', {
        correlationId,
        operation: 'deleteAccount',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  /**
   * Refresh authentication token
   */
  static async refreshToken(): Promise<any> {
    const correlationId = logger.getCorrelationId();
    
    logger.info('Token refresh started', {
      correlationId,
      operation: 'refreshToken'
    });

    try {
      const result = await apiClient.executeRequest<any>({
        method: 'POST',
        url: '/auth/refresh'
      }, correlationId);

      logger.info('Token refresh completed successfully', {
        correlationId,
        operation: 'refreshToken'
      });

      return result;
    } catch (error) {
      logger.error('Token refresh failed', {
        correlationId,
        operation: 'refreshToken',
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }

  static async forgotPassword(email: string): Promise<any> {
    return apiClient.executeRequest<any>({
      method: 'POST',
      url: '/auth/forgot-password',
      data: { email },
    }, logger.getCorrelationId());
  }

  static async resetPassword(token: string, newPassword: string): Promise<any> {
    return apiClient.executeRequest<any>({
      method: 'POST',
      url: '/auth/reset-password',
      data: { token, new_password: newPassword },
    }, logger.getCorrelationId());
  }
}

export class DocumentAPI {


  static async requestUploadUrl(file: File, folderId?: string): Promise<{ upload_url: string; document_id: string; file_name: string }> {
    const correlationId = logger.getCorrelationId();
    return apiClient.executeRequest({
      method: 'POST',
      url: '/documents/presign',
      data: { file_name: file.name, file_size: file.size, folder_id: folderId },
    }, correlationId);
  }

  static async confirmUpload(documentId: string, file: File, folderId?: string): Promise<DocumentUploadResponse> {
    const correlationId = logger.getCorrelationId();
    return apiClient.executeRequest({
      method: 'POST',
      url: '/documents/presign/confirm',
      data: { document_id: documentId, file_name: file.name, file_size: file.size, folder_id: folderId },
    }, correlationId);
  }

  static async requestAnonymousUploadUrl(file: File): Promise<{ upload_url: string; document_id: string; file_name: string }> {
    const correlationId = logger.getCorrelationId();
    return apiClient.executeRequest({
      method: 'POST',
      url: '/documents/anonymous/presign',
      data: { file_name: file.name, file_size: file.size },
    }, correlationId);
  }

  static async confirmAnonymousUpload(documentId: string, file: File): Promise<AnonymousDocumentResponse> {
    const correlationId = logger.getCorrelationId();
    return apiClient.executeRequest({
      method: 'POST',
      url: '/documents/anonymous/presign/confirm',
      data: { document_id: documentId, file_name: file.name, file_size: file.size },
    }, correlationId);
  }

  /**
   * Get documents list with validation and caching
   */
  static async getDocuments(folderId?: string): Promise<DocumentListResponse> {
    const correlationId = logger.getCorrelationId();
    
    // Validate folder ID if provided
    if (folderId && !validator.validateDocumentId(folderId, { correlationId })) {
      throw new ValidationError('Invalid folder ID format', 'folderId', 'INVALID_FOLDER_ID');
    }

    const cacheKey = `documents_${folderId || 'root'}`;
    
    logger.info('Get documents started', {
      correlationId,
      operation: 'getDocuments',
      metadata: { folderId }
    });

    try {
      const params = folderId ? { folderId } : {};
      const result = await apiClient.executeRequest<DocumentListResponse>({
        method: 'GET',
        url: '/documents',
        params
      }, correlationId);

      performanceMonitor.trackCacheAccess(cacheKey, false, correlationId); // Not cached for now

      logger.info('Get documents completed successfully', {
        correlationId,
        operation: 'getDocuments',
        metadata: {
          folderId,
          documentCount: result.documents?.length || 0
        }
      });

      return result;
    } catch (error) {
      logger.error('Get documents failed', {
        correlationId,
        operation: 'getDocuments',
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        metadata: { folderId }
      });
      throw error;
    }
  }

  /**
   * Get document metadata with validation
   */
  static async getDocument(documentId: string): Promise<DocumentMetadataResponse> {
    const correlationId = logger.getCorrelationId();
    
    // Validate document ID
    if (!validator.validateDocumentId(documentId, { correlationId })) {
      throw new ValidationError('Invalid document ID format', 'documentId', 'INVALID_DOCUMENT_ID');
    }

    logger.info('Get document started', {
      correlationId,
      operation: 'getDocument',
      documentId
    });

    try {
      const result = await apiClient.executeRequest<DocumentMetadataResponse>({
        method: 'GET',
        url: `/documents/${documentId}`
      }, correlationId);

      logger.info('Get document completed successfully', {
        correlationId,
        operation: 'getDocument',
        documentId,
        metadata: {
          documentName: result.name,
          pageCount: result.page_count
        }
      });

      return result;
    } catch (error) {
      logger.error('Get document failed', {
        correlationId,
        operation: 'getDocument',
        documentId,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }

  /**
   * Get anonymous document metadata with validation
   */
  static async getAnonymousDocument(documentId: string): Promise<AnonymousDocumentResponse> {
    const correlationId = logger.getCorrelationId();
    
    // Validate document ID
    if (!validator.validateDocumentId(documentId, { correlationId })) {
      throw new ValidationError('Invalid document ID format', 'documentId', 'INVALID_DOCUMENT_ID');
    }

    logger.info('Get anonymous document started', {
      correlationId,
      operation: 'getAnonymousDocument',
      documentId
    });

    try {
      const result = await apiClient.executeRequest<AnonymousDocumentResponse>({
        method: 'GET',
        url: `/documents/anonymous/${documentId}`
      }, correlationId);

      logger.info('Get anonymous document completed successfully', {
        correlationId,
        operation: 'getAnonymousDocument',
        documentId,
        metadata: {
          documentName: result.name,
          pageCount: result.page_count,
          expiresAt: result.expires_at
        }
      });

      return result;
    } catch (error) {
      logger.error('Get anonymous document failed', {
        correlationId,
        operation: 'getAnonymousDocument',
        documentId,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }

  /**
   * Delete document with validation
   */
  static async deleteDocument(documentId: string): Promise<DocumentDeleteResponse> {
    const correlationId = logger.getCorrelationId();
    
    // Validate document ID
    if (!validator.validateDocumentId(documentId, { correlationId })) {
      throw new ValidationError('Invalid document ID format', 'documentId', 'INVALID_DOCUMENT_ID');
    }

    logger.info('Delete document started', {
      correlationId,
      operation: 'deleteDocument',
      documentId
    });

    try {
      const result = await apiClient.executeRequest<DocumentDeleteResponse>({
        method: 'DELETE',
        url: `/documents/${documentId}`
      }, correlationId);

      logger.info('Delete document completed successfully', {
        correlationId,
        operation: 'deleteDocument',
        documentId
      });

      return result;
    } catch (error) {
      logger.error('Delete document failed', {
        correlationId,
        operation: 'deleteDocument',
        documentId,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }

  /**
   * Move document to folder with validation
   */
  static async moveDocument(documentId: string, folderId: string): Promise<DocumentMoveResponse> {
    const correlationId = logger.getCorrelationId();
    
    // Validate IDs
    if (!validator.validateDocumentId(documentId, { correlationId })) {
      throw new ValidationError('Invalid document ID format', 'documentId', 'INVALID_DOCUMENT_ID');
    }
    
    if (!validator.validateDocumentId(folderId, { correlationId })) {
      throw new ValidationError('Invalid folder ID format', 'folderId', 'INVALID_FOLDER_ID');
    }

    logger.info('Move document started', {
      correlationId,
      operation: 'moveDocument',
      documentId,
      metadata: { folderId }
    });

    try {
      const result = await apiClient.executeRequest<DocumentMoveResponse>({
        method: 'PUT',
        url: `/documents/${documentId}/folder`,
        data: { folderId }
      }, correlationId);

      logger.info('Move document completed successfully', {
        correlationId,
        operation: 'moveDocument',
        documentId,
        metadata: { folderId }
      });

      return result;
    } catch (error) {
      logger.error('Move document failed', {
        correlationId,
        operation: 'moveDocument',
        documentId,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        metadata: { folderId }
      });
      throw error;
    }
  }

  // Page-level access methods with comprehensive validation and caching
  
  /**
   * Get document metadata with validation and caching
   */
  static async getDocumentMetadata(documentId: string): Promise<DocumentMetadataResponse> {
    const correlationId = logger.getCorrelationId();
    
    // Validate document ID
    if (!validator.validateDocumentId(documentId, { correlationId })) {
      throw new ValidationError('Invalid document ID format', 'documentId', 'INVALID_DOCUMENT_ID');
    }

    const cacheKey = `metadata_${documentId}`;
    
    logger.info('Get document metadata started', {
      correlationId,
      operation: 'getDocumentMetadata',
      documentId
    });

    try {
      const result = await apiClient.executeRequest<DocumentMetadataResponse>({
        method: 'GET',
        url: `/documents/${documentId}/metadata`
      }, correlationId);

      performanceMonitor.trackCacheAccess(cacheKey, false, correlationId);

      logger.info('Get document metadata completed successfully', {
        correlationId,
        operation: 'getDocumentMetadata',
        documentId,
        metadata: {
          documentName: result.name,
          pageCount: result.page_count,
          fileSize: result.file_size
        }
      });

      return result;
    } catch (error) {
      logger.error('Get document metadata failed', {
        correlationId,
        operation: 'getDocumentMetadata',
        documentId,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }

  /**
   * Get anonymous document metadata with validation and caching
   */
  static async getAnonymousDocumentMetadata(documentId: string): Promise<AnonymousDocumentResponse> {
    const correlationId = logger.getCorrelationId();
    
    // Validate document ID
    if (!validator.validateDocumentId(documentId, { correlationId })) {
      throw new ValidationError('Invalid document ID format', 'documentId', 'INVALID_DOCUMENT_ID');
    }

    const cacheKey = `anonymous_metadata_${documentId}`;
    
    logger.info('Get anonymous document metadata started', {
      correlationId,
      operation: 'getAnonymousDocumentMetadata',
      documentId
    });

    try {
      const result = await apiClient.executeRequest<AnonymousDocumentResponse>({
        method: 'GET',
        url: `/documents/anonymous/${documentId}/metadata`
      }, correlationId);

      performanceMonitor.trackCacheAccess(cacheKey, false, correlationId);

      logger.info('Get anonymous document metadata completed successfully', {
        correlationId,
        operation: 'getAnonymousDocumentMetadata',
        documentId,
        metadata: {
          documentName: result.name,
          pageCount: result.page_count,
          fileSize: result.file_size,
          expiresAt: result.expires_at
        }
      });

      return result;
    } catch (error) {
      logger.error('Get anonymous document metadata failed', {
        correlationId,
        operation: 'getAnonymousDocumentMetadata',
        documentId,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }

  /**
   * Get document page with validation and caching
   */
  static async getDocumentPage(documentId: string, pageNum: number, previewWatermark?: boolean): Promise<Blob> {
    const correlationId = logger.getCorrelationId();
    
    // Validate inputs
    if (!validator.validateDocumentId(documentId, { correlationId })) {
      throw new ValidationError('Invalid document ID format', 'documentId', 'INVALID_DOCUMENT_ID');
    }

    if (!validator.validatePageNumber(pageNum, 1000, { correlationId })) { // Max 1000 pages
      throw new ValidationError('Invalid page number', 'pageNum', 'INVALID_PAGE_NUMBER');
    }

    const cacheKey = `page_${documentId}_${pageNum}`;
    
    logger.info('Get document page started', {
      correlationId,
      operation: 'getDocumentPage',
      documentId,
      pageNum
    });

    try {
      const result = await apiClient.executeRequest<Text>({
        method: 'GET',
        url: `/documents/${documentId}/pages/${pageNum}${previewWatermark ? '?preview_watermark=true' : ''}`,
        responseType: 'text',
      }, correlationId);

      performanceMonitor.trackCacheAccess(cacheKey, false, correlationId);
      const image = Uint8Array.from(atob(result as unknown as string), c => c.charCodeAt(0));
      const blob = new Blob([image], { type: 'image/png' });

      logger.info('Get document page completed successfully', {
        correlationId,
        operation: 'getDocumentPage',
        documentId,
        pageNum,
        metadata: {
          blobSize: blob.size,
          blobType: blob.type
        }
      });

      // Ensure correct MIME type so FileReader.readAsDataURL produces data:image/png;base64,...
      return blob;
    } catch (error) {
      logger.error('Get document page failed', {
        correlationId,
        operation: 'getDocumentPage',
        documentId,
        pageNum,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }

  /**
   * Get anonymous document page with validation and caching
   */
  static async getAnonymousDocumentPage(documentId: string, pageNum: number): Promise<Blob> {
    const correlationId = logger.getCorrelationId();
    
    // Validate inputs
    if (!validator.validateDocumentId(documentId, { correlationId })) {
      throw new ValidationError('Invalid document ID format', 'documentId', 'INVALID_DOCUMENT_ID');
    }

    if (!validator.validatePageNumber(pageNum, 1000, { correlationId })) { // Max 1000 pages
      throw new ValidationError('Invalid page number', 'pageNum', 'INVALID_PAGE_NUMBER');
    }

    const cacheKey = `anonymous_page_${documentId}_${pageNum}`;
    
    logger.info('Get anonymous document page started', {
      correlationId,
      operation: 'getAnonymousDocumentPage',
      documentId,
      pageNum
    });

    try {
      const result = await apiClient.executeRequest<Text>({
        method: 'GET',
        url: `/documents/anonymous/${documentId}/pages/${pageNum}`,
        responseType: 'text',
      }, correlationId);

      performanceMonitor.trackCacheAccess(cacheKey, false, correlationId);
      const image = Uint8Array.from(atob(result as unknown as string), c => c.charCodeAt(0));
      const blob = new Blob([image], { type: 'image/png' });
      logger.info('Get anonymous document page completed successfully', {
        correlationId,
        operation: 'getAnonymousDocumentPage',
        documentId,
        pageNum,
        metadata: {
          blobSize: blob.size,
          blobType: blob.type
        }
      });


      // Ensure correct MIME type so FileReader.readAsDataURL produces data:image/png;base64,...
      return blob;
    } catch (error) {
      logger.error('Get anonymous document page failed', {
        correlationId,
        operation: 'getAnonymousDocumentPage',
        documentId,
        pageNum,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }

  /**
   * Track page view with validation
   */
  static async trackPageView(documentId: string, pageNum: number, duration: number): Promise<PageViewTrackingResponse> {
    const correlationId = logger.getCorrelationId();
    
    // Validate inputs
    if (!validator.validateDocumentId(documentId, { correlationId })) {
      throw new ValidationError('Invalid document ID format', 'documentId', 'INVALID_DOCUMENT_ID');
    }

    if (!validator.validatePageNumber(pageNum, 1000, { correlationId })) {
      throw new ValidationError('Invalid page number', 'pageNum', 'INVALID_PAGE_NUMBER');
    }

    if (duration < 0 || duration > 3600000) { // Max 1 hour
      throw new ValidationError('Invalid duration', 'duration', 'INVALID_DURATION');
    }

    logger.info('Track page view started', {
      correlationId,
      operation: 'trackPageView',
      documentId,
      pageNum,
      duration
    });

    try {
      const result = await apiClient.executeRequest<PageViewTrackingResponse>({
        method: 'POST',
        url: `/documents/${documentId}/pages/${pageNum}/view`,
        data: { duration }
      }, correlationId);

      logger.info('Track page view completed successfully', {
        correlationId,
        operation: 'trackPageView',
        documentId,
        pageNum,
        duration
      });

      return result;
    } catch (error) {
      logger.error('Track page view failed', {
        correlationId,
        operation: 'trackPageView',
        documentId,
        pageNum,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        metadata: { duration }
      });
      throw error;
    }
  }

  /**
   * Download document with validation
   */
  static async downloadDocument(documentId: string): Promise<string> {
    const correlationId = logger.getCorrelationId();

    if (!validator.validateDocumentId(documentId, { correlationId })) {
      throw new ValidationError('Invalid document ID format', 'documentId', 'INVALID_DOCUMENT_ID');
    }

    logger.info('Download document started', {
      correlationId,
      operation: 'downloadDocument',
      documentId
    });

    try {
      const result = await apiClient.executeRequest<{ download_url: string; file_name: string }>({
        method: 'GET',
        url: `/documents/${documentId}/download`,
      }, correlationId);

      logger.info('Download document URL obtained', {
        correlationId,
        operation: 'downloadDocument',
        documentId,
      });

      return result.download_url;
    } catch (error) {
      logger.error('Download document failed', {
        correlationId,
        operation: 'downloadDocument',
        documentId,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }

  /**
   * Download page with validation
   */
  static async downloadPage(documentId: string, pageNum: number): Promise<Blob> {
    const correlationId = logger.getCorrelationId();
    
    // Validate inputs
    if (!validator.validateDocumentId(documentId, { correlationId })) {
      throw new ValidationError('Invalid document ID format', 'documentId', 'INVALID_DOCUMENT_ID');
    }

    if (!validator.validatePageNumber(pageNum, 1000, { correlationId })) {
      throw new ValidationError('Invalid page number', 'pageNum', 'INVALID_PAGE_NUMBER');
    }

    logger.info('Download page started', {
      correlationId,
      operation: 'downloadPage',
      documentId,
      pageNum
    });

    try {
      const result = await apiClient.executeRequest<Blob>({
        method: 'GET',
        url: `/documents/${documentId}/pages/${pageNum}/download`,
        responseType: 'blob',
      }, correlationId);

      logger.info('Download page completed successfully', {
        correlationId,
        operation: 'downloadPage',
        documentId,
        pageNum,
        metadata: {
          blobSize: result.size,
          blobType: result.type
        }
      });

      return result;
    } catch (error) {
      logger.error('Download page failed', {
        correlationId,
        operation: 'downloadPage',
        documentId,
        pageNum,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }

  /**
   * Validate print access with validation
   */
  static async validatePrintAccess(documentId: string): Promise<PrintAccessValidationResponse> {
    const correlationId = logger.getCorrelationId();
    
    // Validate document ID
    if (!validator.validateDocumentId(documentId, { correlationId })) {
      throw new ValidationError('Invalid document ID format', 'documentId', 'INVALID_DOCUMENT_ID');
    }

    logger.info('Validate print access started', {
      correlationId,
      operation: 'validatePrintAccess',
      documentId
    });

    try {
      const result = await apiClient.executeRequest<PrintAccessValidationResponse>({
        method: 'POST',
        url: `/documents/${documentId}/print`
      }, correlationId);

      logger.info('Validate print access completed successfully', {
        correlationId,
        operation: 'validatePrintAccess',
        documentId
      });

      return result;
    } catch (error) {
      logger.error('Validate print access failed', {
        correlationId,
        operation: 'validatePrintAccess',
        documentId,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }
}

// Keep the remaining API classes unchanged for now, but they should follow the same pattern
export class FolderAPI {
  static async getFolders() {
    const response = await apiClient.getClient().get('/folders');
    return response.data;
  }

  static async createFolder(name: string, parentFolderId?: string) {
    const response = await apiClient.getClient().post('/folders', { name, parentFolderId });
    return response.data;
  }

  static async updateFolder(folderId: string, name: string) {
    const response = await apiClient.getClient().put(`/folders/${folderId}`, { name });
    return response.data;
  }

  static async deleteFolder(folderId: string) {
    const response = await apiClient.getClient().delete(`/folders/${folderId}`);
    return response.data;
  }
}

export class PermissionAPI {
  static async getDocumentPermissions(documentId: string) {
    const response = await apiClient.getClient().get(`/documents/${documentId}/permissions`);
    return response.data;
  }

  static async shareDocument(documentId: string, viewerId: string, permissions: {
    canView: boolean;
    canDownload: boolean;
    canPrint: boolean;
    watermark: boolean;
    watermarkText?: string;
    allowedPages?: number[];
    deniedPages?: number[];
    expiresAt?: string;
  }) {
    const response = await apiClient.getClient().post(`/documents/${documentId}/share`, {
      viewer_id: viewerId,
      can_view: permissions.canView,
      can_download: permissions.canDownload,
      can_print: permissions.canPrint,
      watermark: permissions.watermark,
      watermark_text: permissions.watermarkText || null,
      allowed_pages: permissions.allowedPages,
      denied_pages: permissions.deniedPages,
      expires_at: permissions.expiresAt || null,
    });
    return response.data;
  }

  static async updatePermissions(documentId: string, viewerId: string, permissions: {
    canView: boolean;
    canDownload: boolean;
    canPrint: boolean;
    watermark: boolean;
    allowedPages?: number[];
    deniedPages?: number[];
    expiresAt?: string;
  }) {
    const response = await apiClient.getClient().put(`/documents/${documentId}/permissions/${viewerId}`, {
      can_view: permissions.canView,
      can_download: permissions.canDownload,
      can_print: permissions.canPrint,
      watermark: permissions.watermark,
      allowed_pages: permissions.allowedPages,
      denied_pages: permissions.deniedPages,
      expires_at: permissions.expiresAt || null,
    });
    return response.data;
  }

  static async revokeAccess(documentId: string, viewerId: string) {
    const response = await apiClient.getClient().delete(`/documents/${documentId}/permissions/${viewerId}`);
    return response.data;
  }

  static async searchUsers(query: string) {
    const response = await apiClient.getClient().get('/users/search', { 
      params: { q: query } 
    });
    return response.data;
  }

  static async getSharedWithMe() {
    const response = await apiClient.executeRequest<any>({
      method: 'GET',
      url: '/documents/shared-with-me',
    }, logger.getCorrelationId());
    return response;
  }
}

export class AnalyticsAPI {
  static async getDocumentAnalytics(documentId: string) {
    const response = await apiClient.getClient().get(`/documents/${documentId}/analytics`);
    return response.data;
  }

  static async getDocumentViewers(documentId: string) {
    const response = await apiClient.getClient().get(`/documents/${documentId}/analytics/viewers`);
    return response.data;
  }

  static async getPendingViewers(documentId: string) {
    const response = await apiClient.getClient().get(`/documents/${documentId}/analytics/pending-viewers`);
    return response.data;
  }

  static async getPageAnalytics(documentId: string, pageNum: number) {
    const response = await apiClient.getClient().get(`/documents/${documentId}/pages/${pageNum}/analytics`);
    return response.data;
  }

  static async getPageHeatmap(documentId: string) {
    const response = await apiClient.getClient().get(`/documents/${documentId}/analytics/heatmap`);
    return response.data;
  }

  static async getUserReadingPattern(documentId: string, userId: string) {
    const response = await apiClient.getClient().get(`/documents/${documentId}/analytics/users/${userId}/pattern`);
    return response.data;
  }

  static async getEngagementMetrics(documentId: string) {
    const response = await apiClient.getClient().get(`/documents/${documentId}/analytics/engagement`);
    return response.data;
  }

  static async getGeographicAnalytics(documentId: string) {
    const response = await apiClient.getClient().get(`/documents/${documentId}/analytics/geographic`);
    return response.data;
  }

  static async getActivityTimeline(documentId: string) {
    const response = await apiClient.getClient().get(`/documents/${documentId}/analytics/timeline`);
    return response.data;
  }
}

export class NotificationAPI {
  static async getNotifications() {
    const response = await apiClient.getClient().get('/notifications');
    return response.data;
  }

  static async markNotificationRead(notificationId: string) {
    const response = await apiClient.getClient().put(`/notifications/${notificationId}/read`);
    return response.data;
  }

  static async updateNotificationPreferences(preferences: any) {
    const response = await apiClient.getClient().put('/notifications/preferences', preferences);
    return response.data;
  }
}

export class ContactAPI {
  static async sendMessage(email: string, subject: string, message: string): Promise<{ message: string }> {
    const response = await apiClient.getClient().post('/contact', { email, subject, message });
    return response.data;
  }
}

// Export the enhanced API client for direct use if needed
export { apiClient };
export default apiClient;